import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { DEFAULT_ZOOM_LEVEL } from 'context/ZoomContext';

export interface SecondaryNavigationOverflowContextValue {
  expandedGroupKey: string | null;
  isCompact: boolean;
  nestedMenuOpenCount: number;
  notifyNestedMenuClose: () => void;
  notifyNestedMenuOpen: () => void;
  // Dispatch so a delayed close only clears its own group
  setExpandedGroupKey: Dispatch<SetStateAction<string | null>>;
  zoomLevel: number;
}

const noop = () => {};

export const SecondaryNavigationOverflowContext = createContext<SecondaryNavigationOverflowContextValue>({
  expandedGroupKey: null,
  isCompact: false,
  nestedMenuOpenCount: 0,
  notifyNestedMenuClose: noop,
  notifyNestedMenuOpen: noop,
  setExpandedGroupKey: noop,
  zoomLevel: DEFAULT_ZOOM_LEVEL,
});

export const useSecondaryNavigationOverflow = () => useContext(SecondaryNavigationOverflowContext);

// Tolerance to avoid treating a rounded-up scroll width as real overflow
const OVERFLOW_TOLERANCE_PX = 8;

// Re-expand only once there's room for the last known full width again
export const useSecondaryNavigationOverflowMeasurement = <TElement extends HTMLElement = HTMLDivElement>(
  zoomLevel: number = DEFAULT_ZOOM_LEVEL
) => {
  const naturalWidthRef = useRef<number | null>(null);
  const disconnectRef = useRef<(() => void) | undefined>(undefined);
  const [isCompact, setIsCompact] = useState(false);
  const [expandedGroupKey, setExpandedGroupKey] = useState<string | null>(null);
  const [nestedMenuOpenCount, setNestedMenuOpenCount] = useState(0);

  const notifyNestedMenuOpen = useCallback(() => {
    setNestedMenuOpenCount(count => count + 1);
  }, []);

  const notifyNestedMenuClose = useCallback(() => {
    setNestedMenuOpenCount(count => Math.max(0, count - 1));
  }, []);

  // Reattach the observer whenever the measured element mounts/unmounts,
  // because we need to account for the actions row to start empty and only
  // later gain the content this hook is intended to measure.
  const measureRef = useCallback((element: TElement | null) => {
    disconnectRef.current?.();
    disconnectRef.current = undefined;

    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const updateIsCompact = () => {
      setIsCompact(currentIsCompact => {
        if (!currentIsCompact) {
          naturalWidthRef.current = element.scrollWidth;
          return element.scrollWidth > element.clientWidth + OVERFLOW_TOLERANCE_PX;
        }

        const naturalWidth = naturalWidthRef.current ?? element.scrollWidth;
        return element.clientWidth < naturalWidth;
      });
    };

    updateIsCompact();
    const resizeObserver = new ResizeObserver(updateIsCompact);
    resizeObserver.observe(element);
    disconnectRef.current = () => resizeObserver.disconnect();
  }, []);

  useEffect(() => () => disconnectRef.current?.(), []);

  const contextValue = useMemo(
    () => ({
      expandedGroupKey,
      isCompact,
      nestedMenuOpenCount,
      notifyNestedMenuClose,
      notifyNestedMenuOpen,
      setExpandedGroupKey,
      zoomLevel,
    }),
    [expandedGroupKey, isCompact, nestedMenuOpenCount, notifyNestedMenuClose, notifyNestedMenuOpen, zoomLevel]
  );

  return { contextValue, measureRef };
};
